import type { Dispatch, SetStateAction } from 'react';
import { createContext, useContext, useEffect } from 'react';

export type ActivityCenterOpenSource =
  | 'navigation'
  | 'reduced_navigation'
  | 'notification';

export type ActivityCenterType = {
  setWasActivityCenterButtonRenderedOnce: Dispatch<SetStateAction<boolean>>;
  isActivityCenterVisible: boolean;
  activityCenterOpenSource?: ActivityCenterOpenSource;
  openActivityCenter: (source?: ActivityCenterOpenSource) => void;
  closeActivityCenter: () => void;
  total: number;
  totalByEvents: Record<string, number | null>;
  setTotalByEvents: Dispatch<SetStateAction<Record<string, number | null>>>;
  origin: string;
  shouldOpenExternal: boolean;
  setShouldOpenExternal: Dispatch<SetStateAction<boolean>>;
};

export const ActivityCenterContext = createContext<ActivityCenterType>({
  setWasActivityCenterButtonRenderedOnce: () => undefined,
  isActivityCenterVisible: false,
  total: 0,
  totalByEvents: {},
  setTotalByEvents: () => undefined,
  activityCenterOpenSource: undefined,
  openActivityCenter: () => undefined,
  closeActivityCenter: () => undefined,
  origin: 'unset',
  shouldOpenExternal: false,
  setShouldOpenExternal: () => undefined,
});

export const useActivityCenter = (): ActivityCenterType => {
  return useContext(ActivityCenterContext);
};

export const useIsActivityCenterVisible = (): boolean => {
  return useContext(ActivityCenterContext).isActivityCenterVisible;
};

export const useActivityCenterTotal = (): number => {
  return useContext(ActivityCenterContext).total;
};

export const useActivityCenterTotalByEvents = (): Record<
  string,
  number | null
> => {
  return useContext(ActivityCenterContext).totalByEvents;
};

export const useSetActivityCenterTotalByEvents = (): Dispatch<
  SetStateAction<Record<string, number | null>>
> => {
  return useContext(ActivityCenterContext).setTotalByEvents;
};

export const useActivityCenterButtonRendered = (): void => {
  const { setWasActivityCenterButtonRenderedOnce } = useContext(
    ActivityCenterContext
  );
  useEffect(() => {
    setWasActivityCenterButtonRenderedOnce(true);
  }, [setWasActivityCenterButtonRenderedOnce]);
};
